import { AfterViewInit, Component, ComponentFactoryResolver, EventEmitter, inject, Injector, Input, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowDown19, faBolt, faRotateLeft, faSave } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ClassicPreset, GetSchemes } from 'rete';
import { Observable } from 'rxjs';
import { SocketSelectionTypes } from '../../core/socket-selection-type';
import { DesignerSelection } from '../../models/designer-selection';
import { Workflow } from '../../models/workflow';
import { WorkflowActionNode } from '../../nodes/models/workflow-action-node';
import { WorkflowStepNode } from '../../nodes/models/workflow-step-node';
import { WorkflowTriggerNode } from '../../nodes/models/workflow-trigger-node';
import { IFormComponent } from './core/form-component.interface';
import { FormUpdatedEvent } from './core/form-updated-event';
import { NodeFormDirective } from './core/node-form.directive';
import { UnappliedChangesConfirmationDialogComponent } from './unapplied-changes-confirmation-dialog/unapplied-changes-confirmation-dialog.component';
import { WorkflowActionFormComponent } from './workflow-action-form/workflow-action-form';
import { WorkflowEventFormComponent } from './workflow-event-form/workflow-event-form';
import { WorkflowStepFormComponent } from './workflow-step-form/workflow-step-form.component';
import { WorkflowTriggerFormComponent } from './workflow-trigger-form/workflow-trigger-form';

type Schemes = GetSchemes<ClassicPreset.Node, ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node>>;

@Component({
    selector: 'app-detail-bar',
    templateUrl: './detail-bar.component.html',
    styleUrls: ['./detail-bar.component.scss'],
    imports: [FaIconComponent, MatTooltipModule, MatButtonModule, NodeFormDirective, NgxSpinnerModule]
})
export class DetailBarComponent implements AfterViewInit {
    private readonly spinner = inject(NgxSpinnerService);

    public saveIcon = faSave;
    public reloadIcon = faRotateLeft;
    public sortStepsIcon = faArrowDown19;
    public activateIcon = faBolt;

    @ViewChild(NodeFormDirective, { static: true })
    nodeFormHost!: NodeFormDirective;

    currentForm: IFormComponent;

    /**
     * Instance du workflow.
     */
    _workflow: Workflow;

    @Input()
    set workflow(value: Workflow) {
        this._workflow = value;
        this.loadDataComponent();
    }

    get workflow(): Workflow {
        return this._workflow;
    }

    _selection: DesignerSelection;

    @Input()
    set selection(value: DesignerSelection) {
        this._selection = value;
        this.loadDataComponent();
    }

    get selection(): DesignerSelection {
        return this._selection;
    }

    @Input()
    activationEnabled: boolean = false;

    @Output()
    saved = new EventEmitter<boolean>();

    @Output()
    activated = new EventEmitter<boolean>();

    @Output()
    loaded = new EventEmitter<boolean>();

    @Output()
    stepSortingRequested = new EventEmitter<boolean>();

    @Output()
    formUpdated = new EventEmitter<FormUpdatedEvent>();

    constructor(
        private injector: Injector,
        public dialog: MatDialog,
        private componentFactoryResolver: ComponentFactoryResolver
    ) {}

    ngAfterViewInit(): void {
        this.loadDataComponent();
    }

    public save() {
        this.saved.emit(true);
    }

    public load() {
        this.loaded.emit(true);
    }

    public activate() {
        this.activated.emit(true);
    }

    public sortSteps() {
        this.stepSortingRequested.emit();
    }

    loadDataComponent() {
        if (this.nodeFormHost) {
            const viewContainerRef = this.nodeFormHost.viewContainerRef;
            viewContainerRef.clear();

            // On vérifie si un formulaire est déjà affiché et que des modifications aient été apportées à ce formulaire. Si c'est le cas, un message de confirmation demande à l'utilisateur s'il veut sauvegarder les modifications qu'il a apporté au formulaire
            if (this.currentForm) {
                // Un formulaire est affiché
                if (this.currentForm.isDirty) {
                    // Le formulaire possède des modifications
                    console.warn("Le formulaire a été modifié par l'utilisateur.");
                    this.openUnapliedChangesConfirmationDialog().subscribe((confirmation: boolean) => {
                        if (confirmation === true) {
                            // Enregistre les modifications.
                            this.fillData(this._selection?.previousSelection);
                        } else {
                            // Annule les modifications apportées par l'utilisateur.
                            this.currentForm.clearData();
                        }

                        // Charge le formulaire.
                        this.loadForm(viewContainerRef);
                    });
                } else {
                    // Charge le formulaire.
                    this.loadForm(viewContainerRef);
                }
            } else {
                // Charge le formulaire.
                this.loadForm(viewContainerRef);
            }
        }
    }

    /**
     * Charge le formulaire.
     * @param viewContainerRef : Reference au conteneur de vue.
     */
    private loadForm(viewContainerRef: ViewContainerRef) {
        if (this.selection?.selectedSocket) {
            this.spinner.show('detail-bar-spinner');
            console.log('loadDataComponent', this.selection?.selectedSocket);
            if (this.selection.selectedSocket.type === SocketSelectionTypes.Input) {
                const action = this.workflow.actions.find((x) => x.id === this.selection?.selectedSocket.socketCode);
                let componentRef = viewContainerRef.createComponent<WorkflowActionFormComponent>(
                    this.componentFactoryResolver.resolveComponentFactory(WorkflowActionFormComponent)
                );
                this.currentForm = componentRef.instance;
                this.currentForm.data = action.data;
                this.currentForm.workflow = this.workflow;
            } else {
                const trigger = this.workflow.triggers.find((x) => x.id === this.selection?.selectedSocket.socketCode);
                let componentRef = viewContainerRef.createComponent<WorkflowTriggerFormComponent>(
                    this.componentFactoryResolver.resolveComponentFactory(WorkflowTriggerFormComponent)
                );
                this.currentForm = componentRef.instance;
                this.currentForm.data = trigger.data;
                this.currentForm.workflow = this.workflow;
            }

            this.spinner.hide('detail-bar-spinner');
        } else if (this.selection?.selectedNode) {
            this.spinner.show('detail-bar-spinner');
            if (this.selection?.selectedNode instanceof WorkflowStepNode) {
                let componentRef = viewContainerRef.createComponent<WorkflowStepFormComponent>(this.componentFactoryResolver.resolveComponentFactory(WorkflowStepFormComponent));
                this.currentForm = componentRef.instance;
                (this.currentForm as WorkflowStepFormComponent).data = this.selection?.selectedNode.workflowStepData;
                this.currentForm.workflow = this.workflow;
            } else if (this.selection?.selectedNode instanceof WorkflowTriggerNode) {
                let componentRef = viewContainerRef.createComponent<WorkflowTriggerFormComponent>(
                    this.componentFactoryResolver.resolveComponentFactory(WorkflowTriggerFormComponent)
                );
                this.currentForm = componentRef.instance;
                this.currentForm.data = this.selection?.selectedNode.workflowTriggerData;
                this.currentForm.workflow = this.workflow;
            } else if (this.selection?.selectedNode instanceof WorkflowActionNode) {
                let componentRef = viewContainerRef.createComponent<WorkflowActionFormComponent>(
                    this.componentFactoryResolver.resolveComponentFactory(WorkflowActionFormComponent)
                );
                this.currentForm = componentRef.instance;
                this.currentForm.data = this.selection?.selectedNode.workflowActionData;
                this.currentForm.workflow = this.workflow;
            }

            this.spinner.hide('detail-bar-spinner');
        } else if (this.selection?.selectedConnection) {
            this.spinner.show('detail-bar-spinner');
            let componentRef = viewContainerRef.createComponent<WorkflowEventFormComponent>(this.componentFactoryResolver.resolveComponentFactory(WorkflowEventFormComponent));
            this.currentForm = componentRef.instance;
            this.currentForm.data = this.selection?.selectedConnection.workflowEventData;
            this.currentForm.workflow = this.workflow;

            this.spinner.hide('detail-bar-spinner');
        }
    }

    public fillData(designerSelection: DesignerSelection = null) {
        if (this.currentForm) {
            this.currentForm.fillData();

            if (designerSelection == null) {
                designerSelection = this.selection;
            }

            if (designerSelection?.selectedNode) {
                if (designerSelection?.selectedNode instanceof WorkflowStepNode) {
                    designerSelection.selectedNode.workflowStepData = (this.currentForm as WorkflowStepFormComponent).data;
                    this.formUpdated.emit(new FormUpdatedEvent('node', 'step', designerSelection?.selectedNode.id, this.currentForm.data));
                } else if (designerSelection?.selectedNode instanceof WorkflowTriggerNode) {
                    designerSelection.selectedNode.workflowTriggerData = (this.currentForm as WorkflowTriggerFormComponent).data;
                    this.formUpdated.emit(new FormUpdatedEvent('node', 'trigger', designerSelection?.selectedNode.id, (this.currentForm as WorkflowTriggerFormComponent).data));
                } else if (designerSelection?.selectedNode instanceof WorkflowActionNode) {
                    designerSelection.selectedNode.workflowActionData = (this.currentForm as WorkflowActionFormComponent).data;
                    this.formUpdated.emit(new FormUpdatedEvent('node', 'action', designerSelection?.selectedNode.id, (this.currentForm as WorkflowActionFormComponent).data));
                }
            } else if (designerSelection?.selectedConnection) {
                designerSelection.selectedConnection.workflowEventData = (this.currentForm as WorkflowEventFormComponent).data;
                this.formUpdated.emit(new FormUpdatedEvent('connection', null, designerSelection?.selectedConnection.id, (this.currentForm as WorkflowEventFormComponent).data));
            } else if (designerSelection?.selectedSocket) {
                if (designerSelection.selectedSocket.type === SocketSelectionTypes.Input) {
                    this.formUpdated.emit(new FormUpdatedEvent('node', 'action', designerSelection?.selectedSocket.nodeId, (this.currentForm as WorkflowActionFormComponent).data));
                } else {
                    this.formUpdated.emit(
                        new FormUpdatedEvent('node', 'trigger', designerSelection?.selectedSocket.nodeId, (this.currentForm as WorkflowTriggerFormComponent).data)
                    );
                }
            }
        }
    }

    private openUnapliedChangesConfirmationDialog(): Observable<boolean> {
        const dialogRef = this.dialog.open(UnappliedChangesConfirmationDialogComponent, {
            data: null,
            panelClass: 'empty-dialog',
        });

        return dialogRef.afterClosed();
    }
}
